import { useQuery } from '@tanstack/react-query';
import { organizationService } from '../services/organizationService';
import { 
  fetchOrganizationsList, 
  fetchOrganizationsListPaginated,
  OrganizationListDto,
  OrganizationListParams,
  PaginatedOrganizationListResponse
} from '../services/organization.service';

interface UseOrganizationsParams {
  search?: string;
  sortBy?: string;
  page?: number;
  limit?: number;
}

export const useOrganizations = ({
  search = '',
  sortBy = 'name',
  page = 1,
  limit = 10,
}: UseOrganizationsParams = {}) => {
  return useQuery({
    queryKey: ['organizations', { search, sortBy, page, limit }],
    queryFn: () => organizationService.getOrganizations({ search, sortBy, page, limit }),
  });
};

/**
 * Hook for fetching organizations list (non-paginated - legacy)
 * @deprecated Use useOrganizationsListPaginated instead for better performance
 */
export const useOrganizationsList = (parentOrganizationId: string) => {
  return useQuery<OrganizationListDto[], Error>({
    queryKey: ['organizationsList', parentOrganizationId],
    queryFn: () => fetchOrganizationsList(parentOrganizationId),
    enabled: !!parentOrganizationId,
  });
};

/**
 * Hook for fetching paginated organizations list with search support
 * 
 * @param parentOrganizationId - Parent organization ID
 * @param params - Pagination and search parameters
 * @param options - Query options (enabled)
 * @returns Paginated organization list data
 */
export const useOrganizationsListPaginated = (
  parentOrganizationId: string,
  params: OrganizationListParams = {},
  options?: { enabled?: boolean }
) => {
  const isEnabled = options?.enabled !== undefined ? options.enabled : !!parentOrganizationId;
  const shouldRun = isEnabled && !!parentOrganizationId && parentOrganizationId !== 'placeholder';
  
  return useQuery<PaginatedOrganizationListResponse, Error>({
    queryKey: ['organizationsListPaginated', parentOrganizationId, params],
    queryFn: async () => {
      try {
        return await fetchOrganizationsListPaginated(parentOrganizationId, params);
      } catch (error: any) {
        // For 404 errors, return empty data structure instead of throwing
        if (error?.response?.status === 404 || error?.status === 404) {
          return {
            data: {
              content: [],
              totalElements: 0,
              totalPages: 0,
              page: params.page || 0,
              size: params.size || 50,
              message: 'No data',
              type: 'OK'
            }
          } as PaginatedOrganizationListResponse;
        }
        throw error;
      }
    },
    enabled: shouldRun,
    staleTime: 30000, // 30 seconds - reasonable for paginated data
    gcTime: 5 * 60 * 1000, // 5 minutes cache retention
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

export default useOrganizations; 